import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';

const getEnvironment = () => {
    if (window.PRODUCTION) return 'production';
    if (window.STAGING) return 'staging';

    return 'development';
};

const transport = Sentry.makeMultiplexedTransport(
    Sentry.makeFetchTransport,
    (args) => {
        const event = args.getEvent();

        let feature;
        if (event && event.tags) {
            feature = event.tags.feature;
        }

        switch (feature) {
            case 'fintech':
                return [
                    {
                        dsn: window.SENTRY_DSN_FINTECH,
                        release: window.SENTRY_RELEASE,
                    },
                ];
            case 'storefront':
                return [
                    {
                        dsn: window.SENTRY_DSN_STOREFRONT,
                        release: window.SENTRY_RELEASE,
                    },
                ];
            default:
                return [];
        }
    },
);

/**
 * Sanitizes sensitive data from breadcrumbs
 * @param {Object} breadcrumb - The breadcrumb object from Sentry
 * @returns {Object|null} - The sanitized breadcrumb or null to drop it
 */
const sanitizeBreadcrumb = (breadcrumb) => {
    const piiFieldNames = ['ssn', 'dateOfBirth'];
    // Recursively sanitize objects, specifically targeting 'ssn' properties
    const sanitizeObject = (obj) => {
        if (typeof obj !== 'object' || obj === null) return obj;

        const sanitized = Array.isArray(obj) ? [] : {};

        Object.entries(obj).forEach(([key, value]) => {
            if (piiFieldNames.includes(key.toLowerCase()) && value) {
                sanitized[key] = '[CENSORED_PII]';
            } else if (typeof value === 'object') {
                // Recursively sanitize nested objects
                sanitized[key] = sanitizeObject(value);
            } else {
                sanitized[key] = value;
            }
        });

        return sanitized;
    };

    return {
        ...breadcrumb,
        data: breadcrumb.data
            ? sanitizeObject(breadcrumb.data)
            : breadcrumb.data,
        message:
            breadcrumb.message && typeof breadcrumb.message === 'object'
                ? sanitizeObject(breadcrumb.message)
                : breadcrumb.message,
    };
};

export const initializeSentryClient = () =>
    Sentry.init({
        transport,
        dsn: window.SENTRY_DSN_FINTECH,
        release: window.SENTRY_RELEASE,
        integrations: [
            Sentry.replayIntegration(),
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        // Record only 5% of session replays
        replaysSessionSampleRate: 0.05,
        // Record only 10% of error session replays
        replaysOnErrorSampleRate: 0.1,
        tracesSampleRate: 0.2,
        environment: getEnvironment(),
        beforeBreadcrumb: sanitizeBreadcrumb,
        ignoreErrors: [
            'Failed to fetch',
            'Load failed',
            "Shopify root is null, can't add product to cart",
            'Error: Unable to retrieve the origin of the event',
            'Unable to retrieve the origin of the event',
        ],
        beforeSend: (event, hint) => {
            if (hint.originalException === '') {
                return null;
            }

            // This is a known exception that stems from our imported StripeJS package.
            // Skip sending to sentry.
            if (
                hint.originalException ===
                    'Unable to retrieve the origin of the event' ||
                hint.originalException ===
                    'Error: Unable to retrieve the origin of the event'
            ) {
                return null;
            }

            // Intentionally pointing on the event to explicitly
            // state we would want it modified
            const modifiedEvent = event;
            if (
                modifiedEvent &&
                modifiedEvent.request &&
                modifiedEvent.request.url &&
                modifiedEvent.request.url.includes('/shop')
            ) {
                modifiedEvent.tags = {
                    ...modifiedEvent.tags,
                    feature: 'storefront',
                };
            } else if (modifiedEvent && modifiedEvent.tags) {
                modifiedEvent.tags = {
                    ...modifiedEvent.tags,
                    feature: 'fintech',
                };
            }
            return modifiedEvent;
        },
    });
